// Shared choice lists and step builders for questions that several product schemas ask the same way.

// Monthly rent / mortgage payment, asked as a one-tap radio. Stored values are representative
// amounts (band midpoints; the open top band uses a value close to its median), not bucket edges.
// Owners and renters get different bands because their distributions differ (2026-09 production
// leads, last 3 months, all provinces): owners centre on $1,500 (p50) with a long tail past $2,500,
// renters on $800 (p50) with almost nobody past $2,500. Each button holds roughly a fifth to a
// third of its group. Zero is a real answer for both (24% of owners, 11% of renters) and the
// planned Magical Business integration derives housing status from own_home x (payment > 0).
const OWNER_CHOICES = [
    { text: '__opt_rent_mortgage_payment_0_owner__', value: '0' },
    { text: '__opt_rent_mortgage_payment_250__', value: '250' },   // less than $500
    { text: '__opt_rent_mortgage_payment_750__', value: '750' },   // $500 - $999
    { text: '__opt_rent_mortgage_payment_1250__', value: '1250' }, // $1,000 - $1,499
    { text: '__opt_rent_mortgage_payment_2000__', value: '2000' }, // $1,500 - $2,499
    { text: '__opt_rent_mortgage_payment_3000__', value: '3000' }, // $2,500 or more
];

const RENTER_CHOICES = [
    { text: '__opt_rent_mortgage_payment_0_renter__', value: '0' },
    { text: '__opt_rent_mortgage_payment_250__', value: '250' },   // less than $500
    { text: '__opt_rent_mortgage_payment_625__', value: '625' },   // $500 - $749
    { text: '__opt_rent_mortgage_payment_875__', value: '875' },   // $750 - $999
    { text: '__opt_rent_mortgage_payment_1250__', value: '1250' }, // $1,000 - $1,499
    { text: '__opt_rent_mortgage_payment_1800__', value: '1800' }, // $1,500 or more
];

function isOwner(app) {
    return app?.own_home === 'yes';
}

// Owners are asked about their mortgage, renters about their rent.
function rentMortgagePaymentTitle(app) {
    return isOwner(app) ? '__monthly_mortgage_payment__' : '__monthly_rent__';
}

function rentMortgagePaymentChoices(app) {
    return (isOwner(app) ? OWNER_CHOICES : RENTER_CHOICES).map((choice) => ({ ...choice }));
}

// Business industry select, asked the same way by business_loan/mca and payment_processing.
// 15 broad industries (down from 74), alphabetical with Other last. Each is either a
// high-volume group or a distinction a buyer needs: Real estate and Professional
// services feed campaign 2968's not_in filter; Online store and Technology keep Magical
// Credit's (TrustScience) industry categories. Every code must exist in
// CMI\Helpers::BUSINESS_INDUSTRY_OPTS AND lms BUSINESS_INDUSTRY_OPTS (both validate with
// `in:`; lms ships first). Those lists keep the old granular codes, so older leads,
// prepop values and affiliate API leads still validate.
const BUSINESS_INDUSTRY_CHOICES = [
    {"value":"AUTOMOTIVE", "text":"__opt_business_industry_automotive__"},
    {"value":"BEAUTY_SALON_BARBER_SHOP", "text":"__opt_business_industry_beauty__"},
    // Rare (4 leads in 6 months) but kept separate on purpose: cannabis, vape and smoke
    // shops are among the industries business lenders most often restrict, and a lead
    // filed under Retail could never be told apart afterwards.
    {"value":"CANNABIS_VAPE_RETAIL", "text":"__opt_business_industry_cannabis__"},
    {"value":"GENERAL_CONTRACTOR", "text":"__opt_business_industry_construction__"},
    {"value":"RECREATION", "text":"__opt_business_industry_recreation__"},
    {"value":"MEDICAL_HEALTHCARE", "text":"__opt_business_industry_health__"},
    {"value":"MANUFACTURING", "text":"__opt_business_industry_manufacturing__"},
    {"value":"INTERNET_RETAILER", "text":"__opt_business_industry_online_store__"},
    {"value":"PROFESSIONAL_SERVICES", "text":"__opt_business_industry_professional_services__"},
    {"value":"REAL_ESTATE_RELATED", "text":"__opt_business_industry_real_estate__"},
    {"value":"RESTAURANT_CAFE_BAR", "text":"__opt_business_industry_restaurant__"},
    {"value":"RETAIL_STORE", "text":"__opt_business_industry_retail__"},
    {"value":"TECHNOLOGY_COMPANY", "text":"__opt_business_industry_technology__"},
    {"value":"TRUCKING", "text":"__opt_business_industry_transportation__"},
    {"value":"OTHER", "text":"__opt_business_industry_other__"},
];

function businessIndustryChoices() {
    return BUSINESS_INDUSTRY_CHOICES.map((choice) => ({ ...choice }));
}

// The whole step. A radio click always advances the step and skips validation
// (LcField.handleInputFromRadioButton), so the radio must be alone on its step with the Next
// button hidden; the title carries the question, so the field has no label.
function rentMortgagePaymentStep({ hideIfCondition } = {}) {
    const step = {
        beforeCreate: function() {},
        beforeNextStep: function() {},
        title: rentMortgagePaymentTitle,
        fields: [
            {
                name: 'rent_mortgage_payment',
                placeholder: '',
                type: 'radio',
                value: null,
                validationRules: 'required',
                fieldOuterClass: 'col-md-8 offset-md-2',
                choices: rentMortgagePaymentChoices,
                hideIfHasValue: true,
            },
        ],
        hideButtonsSection: true,
    };

    if (typeof hideIfCondition === 'function') {
        step.hideIfCondition = hideIfCondition;
    }

    return step;
}

export { businessIndustryChoices, rentMortgagePaymentTitle, rentMortgagePaymentChoices, rentMortgagePaymentStep };
